import { ICustomerModel, ICustomerDuplicateCheckModel } from "@models";
import { CustomersService } from "./customers.service";
import { ConflictingCustomerDialogComponent } from "@shared-components/conflicting-customers-dialog/conflicting-customers-dialog.component";

export type DuplicateCustomerCheckResult = 'proceed' | 'cancel' | 'add' | number;

export function getDuplicateCheckKey(customer: ICustomerModel): string {
	return [
		customer.address1,
		customer.companyName,
		customer.firstName,
		customer.lastName
	].map(x => (x || '').trim().toLowerCase()).join('|');
}

export function getDismissedDuplicateKey(duplicates: ICustomerDuplicateCheckModel[]): string {
	return duplicates
		.map(d => d.customer.customerId)
		.sort((a, b) => a - b)
		.join(',');
}

export function getDismissedDuplicateSessionKey(
	customer: ICustomerModel,
	duplicates: ICustomerDuplicateCheckModel[]
): string {
	return `${getDuplicateCheckKey(customer)}::${getDismissedDuplicateKey(duplicates)}`;
}

export function pruneDismissedDuplicateKeys(
	dismissedKeys: Set<string>,
	customer: ICustomerModel
): void {
	const address = (customer.address1?.trim() || '').toLowerCase();
	const company = (customer.companyName?.trim() || '').toLowerCase();
	const first = (customer.firstName?.trim() || '').toLowerCase();
	const last = (customer.lastName?.trim() || '').toLowerCase();

	for (const key of [...dismissedKeys]) {
		const fieldKey = key.split('::')[0];
		const [dismissedAddress, dismissedCompany, dismissedFirst, dismissedLast] = fieldKey.split('|');

		if (dismissedAddress && (address.length < 3 || address !== dismissedAddress)) {
			dismissedKeys.delete(key);
			continue;
		}

		if (dismissedCompany && (company.length < 2 || company !== dismissedCompany)) {
			dismissedKeys.delete(key);
			continue;
		}

		if (dismissedFirst && dismissedLast
			&& (first.length < 2 || last.length < 2 || first !== dismissedFirst || last !== dismissedLast)) {
			dismissedKeys.delete(key);
		}
	}
}

export function hasCheckableDuplicateFields(customer: ICustomerModel): boolean {
	const address = customer.address1?.trim();
	const company = customer.companyName?.trim();
	const first = customer.firstName?.trim();
	const last = customer.lastName?.trim();

	return (address?.length >= 3)
		|| (company?.length >= 2)
		|| (first?.length >= 2 && last?.length >= 2);
}

export async function promptDuplicateCustomerCheck(
	customersService: CustomersService,
	dialogRef: ConflictingCustomerDialogComponent,
	customer: ICustomerModel,
	duplicates?: ICustomerDuplicateCheckModel[],
	isStale?: () => boolean
): Promise<DuplicateCustomerCheckResult> {
	if (customer.customerId > 0)
		return 'proceed';

	if (!hasCheckableDuplicateFields(customer))
		return 'proceed';

	const matches = duplicates ?? await customersService.checkDuplicateCustomer(customer);

	if (isStale?.())
		return 'proceed';

	if (!matches?.length)
		return 'proceed';

	const result = await dialogRef.showSuggestionDialog(customer, matches);

	if (result === 'cancel')
		return 'proceed';

	if (result === 'add')
		return 'add';

	if (typeof result === 'number')
		return result;

	return 'proceed';
}
